import React from "react";
import { Button, Form } from "react-bootstrap";
import { useTranslation } from "react-i18next";

import CustomToggleButton from "../customToggleButton/customToggleButton";
import {
  emptyPhase,
  formatInr,
  isPhaseLocked,
  isPhasedPricingEnabled,
  resolvePhaseStatus,
  sumPhaseQuantity,
} from "../../helpers/ticketSalePhases";
import "../../pages/eventPricingHub/eventPricingHub.css";

const TicketPhaseBuilder = ({ ticket, onChange, compact = false }) => {
  const { t } = useTranslation();
  const enabled = isPhasedPricingEnabled(ticket);
  const phases = Array.isArray(ticket?.phases) ? ticket.phases : [];
  const sold = Number(ticket?.ticketssold) || 0;
  const status = resolvePhaseStatus(ticket);
  const totalQty = enabled
    ? sumPhaseQuantity(phases)
    : Number(ticket?.totaltickets) || 0;
  const soldPct =
    totalQty > 0 ? Math.min(100, Math.round((sold / totalQty) * 100)) : 0;

  const updatePhases = (nextPhases) => {
    onChange({
      ...ticket,
      phasedPricingEnabled: true,
      phases: nextPhases,
    });
  };

  const onToggle = (checked) => {
    if (!checked) {
      onChange({
        ...ticket,
        phasedPricingEnabled: false,
      });
      return;
    }
    const seeded =
      phases.length > 0
        ? phases
        : [
            {
              name: "Phase 1",
              quantity: Math.max(1, Number(ticket?.totaltickets) || 1),
              price: Math.max(0, Number(ticket?.ticketprice) || 0),
            },
          ];
    onChange({
      ...ticket,
      phasedPricingEnabled: true,
      phases: seeded,
    });
  };

  const move = (index, dir) => {
    const next = [...phases];
    const target = index + dir;
    if (target < 0 || target >= next.length) return;
    if (isPhaseLocked(ticket, index) || isPhaseLocked(ticket, target)) return;
    [next[index], next[target]] = [next[target], next[index]];
    updatePhases(next);
  };

  return (
    <div className={compact ? "ticket-phase-builder" : "ticket-phase-builder ticket-phase-builder--hub"}>
      <div className="eph-tier-head eph-tier-head--compact">
        <span className="fw-semibold">{t("EVENT_PHASES_ENABLE")}</span>
        <CustomToggleButton
          id={`phased-${ticket?._id || ticket?.tickettype || "new"}`}
          checked={enabled}
          onCheckChange={onToggle}
        />
      </div>

      {enabled ? (
        <>
          {sold > 0 ? (
            <p className="eph-lock-note">{t("EVENT_PHASES_SALES_LOCK_WARN")}</p>
          ) : null}

          <div className="eph-stats">
            <div className="eph-stat">
              <span>{t("EVENT_PHASES_STAT_SOLD")}</span>
              <strong>
                {sold}/{totalQty}
              </strong>
            </div>
            <div className="eph-stat">
              <span>{t("EVENT_PHASES_STAT_LEFT")}</span>
              <strong>{Math.max(0, totalQty - sold)}</strong>
            </div>
            <div className="eph-stat">
              <span>{t("EVENT_PHASES_STAT_LIVE")}</span>
              <strong>{status.currentPhase?.name || "—"}</strong>
            </div>
            <div className="eph-stat">
              <span>{t("EVENT_PHASES_STAT_PRICE")}</span>
              <strong>{formatInr(status.currentPrice)}</strong>
            </div>
          </div>
          <div className="eph-progress" aria-hidden>
            <span style={{ width: `${soldPct}%` }} />
          </div>
          <div className="eph-tier-meta mb-3">
            {t("EVENT_PHASES_REMAINING_IN_PHASE", {
              n: status.remainingInPhase,
            })}
          </div>

          {phases.map((phase, index) => {
            const locked = isPhaseLocked(ticket, index);
            const derived = status.phases?.[index];
            return (
              <div
                key={`${index}-${phase.name}`}
                className={`phase-row${derived?.isCurrent ? " is-current" : ""}${
                  locked ? " is-locked" : ""
                }`}
              >
                <div className="phase-index">
                  {derived?.isCurrent ? t("EVENT_PHASES_NOW") : `P${index + 1}`}
                </div>
                <Form.Group>
                  <Form.Label>{t("EVENT_PHASES_NAME")}</Form.Label>
                  <Form.Control
                    value={phase.name ?? ""}
                    disabled={locked}
                    onChange={(e) => {
                      const next = [...phases];
                      next[index] = { ...next[index], name: e.target.value };
                      updatePhases(next);
                    }}
                  />
                </Form.Group>
                <Form.Group>
                  <Form.Label>{t("EVENT_PHASES_QTY")}</Form.Label>
                  <Form.Control
                    type="number"
                    min={1}
                    inputMode="numeric"
                    value={phase.quantity ?? ""}
                    disabled={locked}
                    onChange={(e) => {
                      const next = [...phases];
                      next[index] = {
                        ...next[index],
                        quantity: e.target.value,
                      };
                      updatePhases(next);
                    }}
                  />
                </Form.Group>
                <Form.Group>
                  <Form.Label>{t("EVENT_PHASES_PRICE")}</Form.Label>
                  <Form.Control
                    type="number"
                    min={0}
                    inputMode="decimal"
                    value={phase.price ?? ""}
                    disabled={locked}
                    onChange={(e) => {
                      const next = [...phases];
                      next[index] = { ...next[index], price: e.target.value };
                      updatePhases(next);
                    }}
                  />
                </Form.Group>
                <div className="phase-actions">
                  <Button
                    size="sm"
                    variant="outline-light"
                    disabled={locked || index === 0}
                    onClick={() => move(index, -1)}
                    aria-label={t("EVENT_PHASES_MOVE_UP")}
                  >
                    <i className="bx bx-chevron-up" />
                  </Button>
                  <Button
                    size="sm"
                    variant="outline-light"
                    disabled={locked || index === phases.length - 1}
                    onClick={() => move(index, 1)}
                    aria-label={t("EVENT_PHASES_MOVE_DOWN")}
                  >
                    <i className="bx bx-chevron-down" />
                  </Button>
                  <Button
                    size="sm"
                    variant="outline-danger"
                    disabled={locked || phases.length <= 1}
                    onClick={() =>
                      updatePhases(phases.filter((_, i) => i !== index))
                    }
                    aria-label={t("EVENT_PHASES_REMOVE")}
                  >
                    <i className="bx bx-trash" />
                  </Button>
                </div>
              </div>
            );
          })}

          <Button
            variant="outline-warning"
            size="sm"
            className="eph-add-phase mt-1"
            onClick={() => updatePhases([...phases, emptyPhase(phases.length)])}
          >
            <i className="bx bx-plus" /> {t("EVENT_PHASES_ADD")}
          </Button>
        </>
      ) : null}
    </div>
  );
};

export default TicketPhaseBuilder;
